import { useRouter } from 'next/router'
import isEqual from 'lodash/isEqual'
import { useEffect, useState } from 'react'

import { getCountryAndLocaleStrings } from '../../utils/locales'
import { ProductBaseIncludes, ResourceTag } from '../../utils/constants'
import { useApi } from '../../services/useApi'
import { decycle } from '../../services/useApiHelpers'
import { Product } from '../../utils/types/Product'
import { getSupplierDeliveryDetails } from '../../lib/service'
import {
  breakShareCartUrlParam,
  generateSharedCartAttributes,
  generateSharedCartIncludedArray,
  getSupplierGroupByProducts,
} from '../../lib/cart'
import { useAuth } from '../../services/useAuth'
import { useCart } from './CartProvider'
import { CartResponse, WithSuppliers } from '../../utils/types/Order'
import { getProductIdsInCart } from './utils/getProductIdsInCart'
import { CartSupplierProducts } from '../../utils/types/Cart'

interface SharedCartData {
  cart: WithSuppliers<CartResponse> | null
  existingCartProductsCount: number
  unavailableProductsSkuList: string[]
  unavailableShipmentSkuList: string[]
  modifiedSharedCart: CartSupplierProducts | null
}

const useSharedCart = () => {
  const [sharedCart, setSharedCart] = useState<SharedCartData>({
    cart: null,
    modifiedSharedCart: null,
    existingCartProductsCount: 0,
    unavailableProductsSkuList: [],
    unavailableShipmentSkuList: [],
  })

  const { locale: countryAndLocale, query } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(countryAndLocale)
  const { cartState: { cart: mainCart } } = useCart()
  const { getResource } = useApi()
  const { isVatIncluded } = useAuth()

  const modifiedSharedCart = sharedCart.cart ? getSupplierGroupByProducts(sharedCart.cart) : null

  const prodIds = mainCart?.included ? getProductIdsInCart(mainCart.included) : []

  const { shareParams } = query

  // Get all the shared SKU codes and quantities from the URL params
  const skuAndQuantityDataList = shareParams?.[0]?.split('+') || []

  // Get a unique array of the relevant shared SKU codes
  const sharedProductSkuCodesList = [...new Set(
    skuAndQuantityDataList?.map((listItem) => {
      const { skuCode } = breakShareCartUrlParam(listItem)
      return skuCode
    }) || [],
  )]

  const prodFilterSeparator = '&filter[%7C%7C][][%3D%3D][product.code]='
  const prodsFilter = prodIds ? `${prodFilterSeparator}${sharedProductSkuCodesList.join(prodFilterSeparator)}` : ''
  const productsQuery = `include=${ProductBaseIncludes.join(',')}${prodsFilter}`

  const { data: products, isLoading: prodIsLoading } = getResource<Product>(
    shareParams ? ResourceTag.product : null,
    productsQuery,
  )

  const suppliers = products.flatMap((product) => product.supplier || [])

  decycle(ResourceTag.product, products)

  useEffect(() => {
    // Check if the products in the products list are available for shipping in the current
    // selected user's country by finding the relevant shipping data for the product's supplier.
    // If not, filter them out and record their SKUs to display them for the user
    let unavailableShipmentSkuList: string[] = []
    const productDataForCurrentCountry = products.filter((product) => {
      const { isAvailable } = getSupplierDeliveryDetails(product.supplier[0], country)

      if (!isAvailable) {
        unavailableShipmentSkuList = [...unavailableShipmentSkuList, product['product.code']]
      }

      return isAvailable
    })

    // If there are less products in the response than the requested SKUs, it means that some
    // products are unavailable, get their SKUs to display them for the user
    let unavailableProductsSkuList: string[] = []
    if (!prodIsLoading && products.length < sharedProductSkuCodesList.length) {
      unavailableProductsSkuList = sharedProductSkuCodesList.filter((sku) => !products?.find((product) => product['product.code'] === sku))
    }

    const sharedCartAttributes = generateSharedCartAttributes(
      productDataForCurrentCountry,
      skuAndQuantityDataList,
      suppliers,
      country,
      locale,
      isVatIncluded,
    )
    const sharedCartIncluded = generateSharedCartIncludedArray(
      productDataForCurrentCountry,
      skuAndQuantityDataList,
      country,
    )

    if (mainCart?.data) {
      const updatedSharedCartData: SharedCartData = {
        cart: {
          ...mainCart,
          data: {
            ...mainCart.data,
            attributes: sharedCartAttributes,
          },
          included: sharedCartIncluded,
          suppliers: suppliers.map((sup) => ({
            ...sup,
          })),
        },
        modifiedSharedCart,
        existingCartProductsCount: new Set(prodIds).size,
        unavailableProductsSkuList,
        unavailableShipmentSkuList,
      }

      if (
        !isEqual(sharedCart, updatedSharedCartData)
        && mainCart
        && !prodIsLoading
      ) {
        setSharedCart(updatedSharedCartData)
      }
    }
  }, [
    products,
    suppliers,
    mainCart,
    prodIsLoading,
  ])

  return sharedCart
}

export default useSharedCart
